import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import '../css/RatingBox.css';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';

const RatingBox = ({ data, previousMsg, scrollToBottom }) => {
  // Existing states
  const [rating, setRating] = useState();
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { t } = useTranslation();

  // Create a ref for the textarea element
  const textAreaRef = useRef(null);

  // Call scrollToBottom after the rating updates and focus the textarea if needed.
  useEffect(() => {
    if (rating !== undefined) {
      // If the rating is less than 3 then feedback is required.
      if (rating < 3) {
        scrollToBottom();
        // Use setTimeout to wait until the DOM updates and scrolling completes.
        setTimeout(() => {
          if (textAreaRef.current) {
            textAreaRef.current.focus();
          }
        }, 0);
      }
    }
  }, [rating, scrollToBottom]);

  const handleSubmit = async (currentRating) => {
    if (isProcessing) return;
    setIsProcessing(true);

    if (currentRating < 3 && comment.trim() === '') {
      setError(t("Please provide feedback for low ratings."));
      setIsProcessing(false);
      return;
    }

    const sessionID = sessionStorage.getItem('sessionID');
    const messageID = previousMsg?.messageID;
    const formName = previousMsg?.formName;
    const custom = previousMsg?.custom;
    const formParams = previousMsg?.leaveRequestData;
    const actionResult = data.actionResult;
    let frontendLog = JSON.stringify({
      "sessionID": sessionID,
      "EmployeeID": localStorage.getItem('EmployeeID'),
      "channel": isMobile ? "Mobile" : "Portal",
      "language": localStorage.getItem('language')
    });

    const payload = {
      sessionID,
      messageID,
      formName,
      formParams,
      actionResult,
      custom,
      rating: currentRating,
      requestType: data.requestType,
      ...(currentRating < 3 ? { comment } : { comment: "" }),
      frontendLog
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/chatbot/logFeedback?code=${process.env.REACT_APP_CHATBOT_FUNCTION_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      const result = await response.json();
      setSubmitted(true);
    } catch (err) {
      console.error("Error submitting feedback:", err);
      setError(t("An error occurred while submitting feedback."));
    }
    setIsProcessing(false);
  };

  const handleStarClick = (star) => {
    setRating(star);
    if (star >= 3 && !isProcessing && !submitted) {
      setError('');
      setComment('');
      handleSubmit(star);
    }
  };

  const handleStarHover = (star) => {
    setHoverRating(star);
  };

  const handleStarHoverOut = () => {
    setHoverRating(0);
  };

  // Cancel handler to reset the form state.
  const handleCancel = () => {
    // Reset state to initial values: empty stars & empty feedback.
    setRating(undefined);
    setHoverRating(0);
    setComment('');
    setError('');
    setSubmitted(false);
  };

  return (
    <div className={`rating-container ${rating<3?'open-rating':''}`}>
      <div className="rating-box">
        {submitted ? (
          <div className="confirmation-message">
            <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
            <span>{t("Thank you for your feedback!")}</span>
          </div>
        ) : (
          <>
            <div className="stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <FontAwesomeIcon
                  key={star}
                  icon={faStar}
                  onClick={() => handleStarClick(star)}
                  onMouseEnter={() => handleStarHover(star)}
                  onMouseLeave={handleStarHoverOut}
                  className={`star ${(hoverRating || rating || 0) >= star ? 'filled' : ''}`}
                />
              ))}
            </div>
            {rating !== null && rating < 3 && (
              <>
                <textarea
                  ref={textAreaRef}
                  placeholder={t("Leave your feedback...")}
                  value={comment}
                  rows={1}
                  className='feedback-textarea'
                  onChange={(e) => setComment(e.target.value)}
                />
                {error && <div className="error">{error}</div>}
                <div className="rating-buttons">
                  <button onClick={() => handleSubmit(rating)} disabled={isProcessing}>
                    {t("Submit")}
                  </button>
                  {/* Updated Cancel button using the new handler */}
                  <button onClick={handleCancel} disabled={isProcessing} className="cancel-btn">
                    {t("Cancel")}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RatingBox;
